import { JobRestorationMedia } from '@app/models';
import { catchError, concatMap, map, mergeMap, take } from 'rxjs/operators';
import { AppState } from '@app/state';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { createAction, props, Store } from '@ngrx/store';
import { JobRestorationMediaService } from '@services/api/job-restoration-media.service';
import {
  ImageUploadService,
  PhotoMeta,
} from '@services/image-upload/image-upload.service';
import { hasPhotoUploadSource } from '@services/upload/photo-source';
import { UploadQueueService } from '@services/upload/upload-queue.service';
import { CreateSuccess, Update } from '@briebug/ngrx-auto-entity';
import { EMPTY } from 'rxjs';

export interface CreateJobRestorationMediaRequest {
  restorationTicket: string;
  employee: string;
  fileName: string;
  photo: PhotoMeta;
}

export const createJobRestorationMedia = createAction(
  '[Job Restoration Media] Create',
  props<{ payload: CreateJobRestorationMediaRequest }>()
);

@Injectable()
export class JobRestorationMediaEffects {
  createRestorationMedia$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createJobRestorationMedia),
        map(action => action.payload),
        // `mergeMap`, not `concatMap`: the creates must not be serialised.
        // Each one is a server round-trip, so six photos took ~15 s to all
        // reach the native queue -- and a force-quit before that window
        // closed lost the ones still waiting. Crews do take a burst and
        // pocket the phone immediately. Running them together cuts the
        // window to roughly one round-trip. It does not close it; that needs
        // the photo to become durable at capture rather than at create.
        mergeMap(payload => {
          // Bail before the POST. A photo the uploader cannot read would
          // otherwise leave a permanent row with imageExists: false -- a grey
          // placeholder that never resolves, which is the exact failure this
          // whole change exists to remove.
          if (!hasPhotoUploadSource(payload.photo)) {
            console.error(
              '[Job Restoration Media] create skipped: photo has neither a native path nor a file',
              payload.fileName
            );
            return EMPTY;
          }

          const rm: JobRestorationMedia = {
            restorationTicket: payload.restorationTicket,
            employee: payload.employee,
            originalFileName: payload.fileName,
            imageExists: false,
          };

          return this.jobRestorationMediaService.create(rm).pipe(
            // `concatMap` with an async callback, not `map`: concatMap awaits
            // the promise, so a rejection reaches the catchError below
            // instead of becoming an unhandled rejection.
            concatMap(async response => {
              this.store$.dispatch(
                new CreateSuccess(JobRestorationMedia, response)
              );

              if (!this.uploads.isNativeEnabled) {
                // Legacy path, kept alive precisely for this: native code
                // cannot be hot-fixed, so the kill switch needs somewhere to
                // fall back to rather than just stopping uploads.
                const photo = { ...payload.photo, id: response.id };
                await this.imageUpload.addPhoto(photo);
                const image = await this.imageUpload.getPhotoFile(response.id);

                // Restoration is the one of the three whose uploadFile
                // returns an Observable<boolean>, and the retired
                // uploadRestorationMedia$ effect was what turned that into
                // the imageExists flip. Subscribed rather than awaited: the
                // old code dispatched an action and let a separate effect
                // handle it, so the create pipeline never blocked on the
                // upload, and it must not start doing so now.
                this.jobRestorationMediaService
                  .uploadFile({ jobRestorationMedia: response, image })
                  .pipe(take(1))
                  .subscribe(completed => {
                    if (completed) {
                      this.store$.dispatch(
                        new Update(JobRestorationMedia, {
                          ...response,
                          imageExists: true,
                        })
                      );
                    }
                  });
                return;
              }

              // Hand the plugin the native path. The photo is never read into
              // the WebView -- a full-resolution capture is several megabytes.
              await this.uploads.enqueue({
                entityType: 'JobRestorationMedia',
                entityId: response.id,
                uploadUrl: response.uploadUrl,
                originalFileName: payload.fileName,
                photo: payload.photo,
              });
            }),
            catchError(error => {
              console.error('[Job Restoration Media] create failed', error);
              return EMPTY;
            })
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private jobRestorationMediaService: JobRestorationMediaService,
    private uploads: UploadQueueService,
    private imageUpload: ImageUploadService,
    private store$: Store<AppState>
  ) {}
}
